import React from 'react';
import { createPortal } from 'react-dom';

/**
 * Small pieces every Risk screen shares. Numbers are the mockup's:
 * https://claude.ai/artifact/Wc4G8S7Mcq6qe2GhujQf3k — copy them, don't tune by ear.
 *
 * Level colours are NOT ours: they are the tenant's own FMEA settings colours (Orcanos admin →
 * FMEA Settings), so a risk reads the same colour here as in Orcanos.
 */

/**
 * A designed tooltip, used instead of the browser's `title` box everywhere in Risk.
 * `tip` is any node (a string, or a small layout); `heading` is an optional bold first line.
 * Opens after the pointer RESTS (a sweep across a table must not flicker a box on every cell),
 * or at once on keyboard focus. Portalled with `position: fixed` so a table's overflow can't
 * clip it; placed below the anchor, flipped above when there is no room, clamped to the viewport.
 */
const TIP_DELAY_MS = 250;
let tipSeq = 0;

export function Tip({ tip, heading, children, className = '', tipClassName = '', as: As = 'span', ...rest }) {
  const [rect, setRect] = React.useState(null);
  const [pos, setPos] = React.useState(null);
  const anchor = React.useRef(null);
  const box = React.useRef(null);
  const timer = React.useRef(null);
  const id = React.useMemo(() => `rsk-tip-${++tipSeq}`, []);

  const show = (now) => {
    clearTimeout(timer.current);
    const open = () => anchor.current && setRect(anchor.current.getBoundingClientRect());
    if (now) open(); else timer.current = setTimeout(open, TIP_DELAY_MS);
  };
  const hide = () => { clearTimeout(timer.current); setRect(null); setPos(null); };
  React.useEffect(() => () => clearTimeout(timer.current), []);
  React.useEffect(() => {
    if (!rect) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') hide(); };
    window.addEventListener('scroll', hide, true);
    document.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('scroll', hide, true); document.removeEventListener('keydown', onKey); };
  }, [rect]);

  // Place from the box's REAL size, after it renders.
  React.useLayoutEffect(() => {
    const el = box.current;
    if (!rect || !el) return;
    const w = el.offsetWidth; const h = el.offsetHeight; const m = 8;
    const vw = window.innerWidth; const vh = window.innerHeight;
    let x = rect.left + rect.width / 2 - w / 2;
    x = Math.max(m, Math.min(x, vw - w - m));
    const below = rect.bottom + 8;
    const above = rect.top - h - 8;
    const up = below + h > vh - m && above >= m;
    setPos({ x, y: up ? above : below, up, arrow: Math.max(12, Math.min(rect.left + rect.width / 2 - x, w - 12)) });
  }, [rect]);

  if (!tip) return <As className={className} {...rest}>{children}</As>;
  return (
    <>
      <As
        ref={anchor}
        className={className}
        onMouseEnter={() => show(false)}
        onMouseLeave={hide}
        onFocus={() => show(true)}
        onBlur={hide}
        aria-describedby={rect ? id : undefined}
        {...rest}
      >
        {children}
      </As>
      {rect && createPortal(
        <div
          ref={box}
          id={id}
          role="tooltip"
          className={`rsk-tip${pos?.up ? ' rsk-tip--up' : ''}${tipClassName ? ` ${tipClassName}` : ''}`}
          style={{ left: pos ? pos.x : rect.left, top: pos ? pos.y : rect.bottom + 8, visibility: pos ? 'visible' : 'hidden', '--rsk-tip-arrow': `${pos ? pos.arrow : 12}px` }}
        >
          {heading && <div className="rsk-tip-head">{heading}</div>}
          <div className="rsk-tip-body">{tip}</div>
        </div>,
        document.body,
      )}
    </>
  );
}

// Readable text on any background colour (the tenant's level colours vary).
export function textOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return '#1A1631';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255; const g = (n >> 8) & 255; const b = n & 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#1A1631' : '#fff';
}

export function LevelPill({ level, title }) {
  if (!level) return <span className="rsk-dim">—</span>;
  const bg = level.color || '#EFECF6';
  const tip = title || (level.source === 'computed'
    ? 'Computed from P and S with your FMEA table — the filter has no stored level.'
    : 'Orcanos’s stored level.');
  return (
    <Tip className="rsk-pill" style={{ background: bg, color: textOn(bg) }} heading={level.label} tip={tip}>
      {level.label}{level.source === 'computed' ? ' *' : ''}
    </Tip>
  );
}

export const CONTROL_STYLE = {
  uncontrolled: { bg: '#7A1F05', fg: '#fff', dot: '#7A1F05' },
  failed: { bg: '#FBE0D5', fg: '#8A2A0B', dot: '#C2410C' },
  unverified: { bg: '#FFF1D6', fg: '#7A4B00', dot: '#D49B2A' },
  not_run: { bg: '#F3F0F8', fg: '#4A4560', dot: '#A8A1BE' },
  mitigated: { bg: '#E3ECFB', fg: '#1F4E9E', dot: '#5B86C9' },
  controlled: { bg: '#DDF3E4', fg: '#17603A', dot: '#17603A' },
  accepted: { bg: '#EEF6F1', fg: '#2E6B4E', dot: '#7FB89E' },
  unscored: { bg: '#F3F0F8', fg: '#6B6580', dot: '#D4D0E0' },
};

export function ControlPill({ control }) {
  if (!control) return null;
  const st = CONTROL_STYLE[control.status] || CONTROL_STYLE.unscored;
  return (
    <Tip className="rsk-pill" style={{ background: st.bg, color: st.fg }} heading={control.label} tip={control.hint}>
      {control.label}
    </Tip>
  );
}

/** The grid's Verification column: mitigations with at least one test, of those looked at. */
export function CoverageBar({ coverage }) {
  if (!coverage || !coverage.total) return <span className="rsk-dim">—</span>;
  const { covered, total, pct } = coverage;
  return (
    <Tip className="rsk-bar-cell" heading={`${pct}% verified`}
      tip={`${covered} of ${total} mitigation${total === 1 ? '' : 's'} traced to a test.`}>
      <span className="rsk-bar"><i className="rsk-seg--cov" style={{ width: `${pct}%` }} /></span>
      <span className="rsk-bar-num">{pct}%</span>
    </Tip>
  );
}

// Orcanos's own status words, as the bar groups them. Order = left to right.
export const RESULT_SEGMENTS = [
  { key: 'pass', label: 'Pass' },
  { key: 'fail', label: 'Fail / Fail (Stop)' },
  { key: 'no_run', label: 'No Run / Skip' },
  { key: 'not_in_exec', label: 'Not in Execution' },
  { key: 'unknown', label: 'Result not read' },
];

/** The grid's Test results column: the risk's distinct tests, by last run status. */
export function ResultsBar({ results }) {
  if (!results || !results.total) return <span className="rsk-dim">—</span>;
  const parts = RESULT_SEGMENTS.filter((s) => results[s.key] > 0);
  return (
    <Tip className="rsk-bar-cell" heading={`${results.total} test${results.total === 1 ? '' : 's'}`}
      tip={parts.map((s) => `${s.label}: ${results[s.key]}`).join(' · ')}>
      <span className="rsk-bar">
        {parts.map((s) => (
          <i key={s.key} className={`rsk-seg--${s.key}`} style={{ width: `${(100 * results[s.key]) / results.total}%` }} />
        ))}
      </span>
      <span className="rsk-bar-num">{results.total}</span>
    </Tip>
  );
}

const DRIVER_NAMES = {
  residual: 'Residual risk',
  gaps: 'Control gaps',
  field: 'Field signal',
  stale: 'Staleness',
};
const DRIVER_MISSING = {
  field: 'Traced Items Info',
  stale: 'Updated date',
};
export const BAND_STYLE = {
  Low: { background: '#DDF3E4', color: '#17603A' },
  Moderate: { background: '#FFE9C2', color: '#7A4B00' },
  Elevated: { background: '#FBE0D5', color: '#8A2A0B' },
  High: { background: '#7A1F05', color: '#fff' },
};

/**
 * Every dashboard score (risk, traceability, SBOM exposure) is SHOWN as health: 100 = nothing
 * wrong, higher is better — people read a 0–100 number as a grade (2026-10-23). The engines, the
 * stored baselines and their sha256 still count PROBLEM points (0 = nothing wrong); this is the one
 * flip, applied at display. Driver and fix points stay problem points: "−12" lost, "+8" if fixed.
 */
export const health = (n) => (n === null || n === undefined ? n : 100 - Math.round(n));

/** The band ranges on the health scale (problem < 25 = Low ⇒ health 76–100). */
export function BandHelp() {
  return (
    <div className="rsk-help-bands">
      <span className="rsk-pill" style={BAND_STYLE.Low}>Low risk 76–100</span>
      <span className="rsk-pill" style={BAND_STYLE.Moderate}>Moderate 51–75</span>
      <span className="rsk-pill" style={BAND_STYLE.Elevated}>Elevated 26–50</span>
      <span className="rsk-pill" style={BAND_STYLE.High}>High risk 0–25</span>
    </div>
  );
}

// What each driver counts against the score — the help popover's table. Mirrors
// risk_engine.py ZONE_WEIGHT / GAP_WEIGHT / _risk_points; change both together.
const DRIVER_HELP = {
  residual: 'Each risk\'s level after mitigation. Unacceptable counts fully, ALARP 40%, acceptable nothing.',
  gaps: 'A risk that needs a control but has none, or whose verifying test failed (fully); a test not linked (60%) or not run (40%).',
  field: 'Complaints traced to a risk — weighted most when the risk is rated unlikely (P1–2) yet complaints still arrive.',
  stale: 'Risks nobody has updated for over a year.',
};

/** The "?" button + click-open popover. `end` anchors it to the right, for a right-hand column. */
export function HelpPop({ label, end = false, children }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <span className="rsk-help" ref={ref}>
      <button
        type="button"
        className="rsk-help-btn"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >?</button>
      {open && (
        <div className={`rsk-help-pop${end ? ' rsk-help-pop--end' : ''}`} role="dialog" aria-label={label}>
          {children}
        </div>
      )}
    </span>
  );
}

function ScoreHelp({ score }) {
  return (
    <HelpPop label="How the product risk score works">
          <p className="rsk-help-lead">
            <strong>Higher is better.</strong> 100 means no open problems; 0 means every risk is
            unacceptable, uncontrolled and drawing complaints. Each part takes points off 100.
          </p>
          <BandHelp />
          <table className="rsk-help-tbl">
            <thead>
              <tr><th>Part</th><th>What counts against you</th><th>Points lost</th></tr>
            </thead>
            <tbody>
              {score.drivers.map((d) => (
                <tr key={d.key}>
                  <td>{DRIVER_NAMES[d.key]}</td>
                  <td>{DRIVER_HELP[d.key]}</td>
                  <td className="rsk-help-num">
                    {d.available ? <><strong>−{d.points}</strong> / {d.max}</> : <em>not counted</em>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="rsk-fine">
            Base weights are 45 · 30 · 15 · 10. A part the filter cannot answer (no Traced Items Info,
            no Updated date) is left out and the others are scaled up to fill 100 — so add that
            column to the filter to have it counted. The score averages over every risk in the filter.
          </p>
    </HelpPop>
  );
}

/** Help for "What would raise the score most" — the per-risk share of the same score. */
export function LowerHelp() {
  return (
    <HelpPop label="How this list works" end>
      <p className="rsk-help-lead">
        <strong>The five risks that take the most off the product risk score.</strong> Fix one and
        the score rises by its number.
      </p>
      <p className="rsk-help-lead">
        A risk's points are its share of the same four parts — residual level, control gaps,
        complaints, not reviewed. The line under its name is the biggest of them.
      </p>
      <p className="rsk-fine">
        Risks with the same problem score the same, because the score averages over every risk in
        the filter. A higher number means more than one part counts against it.
      </p>
    </HelpPop>
  );
}

export function ScoreBlock({ score, trend }) {
  if (!score || score.score === null || score.score === undefined) {
    return <div className="rsk-dim">No risks to score yet.</div>;
  }
  const weights = score.drivers.filter((d) => d.available).map((d) => `${DRIVER_NAMES[d.key].toLowerCase()} ${d.weight}`);
  return (
    <div className="rsk-score">
      <div className="rsk-lbl rsk-score-head"><span>Product risk score</span><ScoreHelp score={score} /></div>
      <div className="rsk-score-main">
        <div className="rsk-score-num">{health(score.score)}</div>
        <div className="rsk-score-of">
          <div>of 100 · higher is better</div>
          <span className="rsk-pill" style={BAND_STYLE[score.band]}>{score.band}</span>
        </div>
        {trend?.length > 0 && (
          <div className="rsk-trend" aria-label="Score at each baseline">
            {trend.slice(-3).map((t) => (
              <div key={t.id}>{t.name} · <strong>{health(t.score) ?? '—'}</strong></div>
            ))}
            <div>
              Now · <strong>{health(score.score)}</strong>
              <TrendDelta from={trend[trend.length - 1]?.score} to={score.score} />
            </div>
          </div>
        )}
      </div>
      <div className="rsk-drivers">
        {score.drivers.map((d) => (
          <div className="rsk-drv" key={d.key}>
            <span>{DRIVER_NAMES[d.key]}</span>
            {d.available ? (
              <>
                <div className="rsk-trk" role="img" aria-label={`${DRIVER_NAMES[d.key]} ${d.points} of ${d.max} points lost`}>
                  <i style={{ width: `${d.max ? (d.points / d.max) * 100 : 0}%` }} />
                </div>
                <span className="rsk-drv-pts"><strong>−{d.points}</strong> / {d.max}</span>
              </>
            ) : (
              <Tip className="rsk-drv-na" heading="Left out of the score"
                tip={DRIVER_MISSING[d.key]
                  ? `The filter has no ${DRIVER_MISSING[d.key]} column — add it to the filter to have this counted.`
                  : 'No data for this part.'}>
                not in filter
              </Tip>
            )}
          </div>
        ))}
      </div>
      <div className="rsk-fine">
        Weights: {weights.join(' · ')} (re-weighted to 100 over the parts the filter can answer).
        A management indicator — risk acceptability stays your decision.
      </div>
    </div>
  );
}

export function Heatmap({ heat, onCell }) {
  if (!heat) return null;
  const sn = heat.s_range || heat.range || 5;
  return (
    <div>
      <div className="rsk-hm" style={{ gridTemplateColumns: `28px repeat(${sn}, minmax(0, 1fr))` }}>
        {heat.rows.map((row) => (
          <React.Fragment key={row[0].p}>
            <div className="rsk-hm-ax">P{row[0].p}</div>
            {row.map((c) => {
              const bg = c.color || '#EFECF6';
              const style = c.n
                ? { background: bg, color: textOn(bg) }
                : { background: `${bg}33`, color: '#A8A1BE' };
              return (
                <Tip
                  as="button"
                  type="button"
                  key={`${c.p}-${c.s}`}
                  className="rsk-hm-c"
                  style={style}
                  heading={`P${c.p} × S${c.s}${c.level ? ` · ${c.level}` : ''}`}
                  aria-label={`${c.n} risk${c.n === 1 ? '' : 's'} · P${c.p} × S${c.s}${c.level ? ` · ${c.level}` : ''}`}
                  tip={c.n ? `${c.n} risk${c.n === 1 ? '' : 's'} — click to show only these${c.mismatch
                    ? `. ${c.mismatch} stored in Orcanos at a level other than ${c.grid_level || 'the FMEA matrix'}, which the FMEA settings give this cell — coloured as Orcanos stores them.`
                    : ''}` : null}
                  onClick={c.n && onCell ? () => onCell(c) : undefined}
                  disabled={!c.n}
                >
                  {c.n || '·'}
                  {c.mismatch > 0 && <span className="rsk-hm-flag" aria-hidden="true">!</span>}
                </Tip>
              );
            })}
          </React.Fragment>
        ))}
        <div />
        {Array.from({ length: sn }, (_, i) => <div className="rsk-hm-ax" key={i}>S{i + 1}</div>)}
      </div>
      {heat.unplaced > 0 && (
        <div className="rsk-fine" style={{ marginTop: 6 }}>
          {heat.unplaced} risk{heat.unplaced === 1 ? '' : 's'} not placed — P or S is empty in Orcanos.
        </div>
      )}
      {heat.outside > 0 && (
        <div className="rsk-fine" style={{ marginTop: 6 }}>
          {heat.outside} risk{heat.outside === 1 ? '' : 's'} not shown — scored above this {heat.p_range} × {heat.s_range} matrix. Edit the panel to show more levels.
        </div>
      )}
    </div>
  );
}

/** The move between two RAW (problem-point) scores, drawn on the health scale: ▲ green = better. */
export function TrendDelta({ from, to }) {
  return <HealthDelta from={health(from)} to={health(to)} />;
}

/** The move between two HEALTH scores: up is better (green), down is worse (red). */
export function HealthDelta({ from, to }) {
  if (from === null || from === undefined || to === null || to === undefined || from === to) return null;
  const d = Math.round(Math.abs(to - from));
  return to > from ? <span className="rsk-better"> ▲ {d}</span> : <span className="rsk-worse"> ▼ {d}</span>;
}

export function LevelLegend({ levels }) {
  return (
    <div className="rsk-legend">
      {(levels || []).filter((l) => l.n !== 0 || l.n === undefined).map((l) => (
        <span key={l.code}>
          <i style={{ background: l.color || '#EFECF6' }} />
          {l.label}{l.n !== undefined ? <strong>{l.n}</strong> : null}
        </span>
      ))}
    </div>
  );
}

export function ControlTiles({ counts, active, onPick }) {
  return (
    <div className="rsk-tiles">
      {(counts || []).map((c) => {
        const st = CONTROL_STYLE[c.status] || CONTROL_STYLE.unscored;
        const on = active === c.status;
        return (
          <button
            type="button"
            key={c.status}
            className={`rsk-tile${on ? ' rsk-tile--on' : ''}`}
            onClick={() => onPick(on ? '' : c.status)}
            aria-pressed={on}
          >
            <span className="rsk-tile-name"><i style={{ background: st.dot }} />{c.label}</span>
            <span className="rsk-tile-n">{c.n}</span>
            <span className="rsk-tile-hint">{c.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Mitigation coverage — how many distinct mitigations have a test, and how those tests last ran.
 * Counted once per mitigation and once per test across the risk file (risk_engine.mitigation_coverage).
 * `running` = the background relation walk has not finished, so the numbers may still move.
 */
export function CoverageCard({ coverage, verification, running }) {
  const c = coverage;
  if (!c || !c.mitigations) {
    return <div className="rsk-cov"><div className="rsk-lbl">Mitigations covered by a test</div><div className="rsk-dim">No mitigation is traced yet.</div></div>;
  }
  const pct = c.pct;
  const res = c.results || {};
  const parts = [
    ['pass', 'passed', '#17603A'], ['fail', 'failed', '#B23A12'], ['not_run', 'not run', '#8A84A0'],
    ['unknown', 'result not read', '#C9C4D8'],
  ].filter(([k]) => res[k]);
  return (
    <div className="rsk-cov" aria-label="Mitigations covered by a test">
      <div className="rsk-lbl">Mitigations covered by a test{running && <span className="rsk-lbl-note"> · updating…</span>}</div>
      {c.checked === 0 ? (
        <div className="rsk-dim">{running ? 'Reading test links in the background…' : 'Tests not checked — no test filter and no relations read.'}</div>
      ) : (
        <>
          <div className="rsk-cov-main">
            <span className="rsk-cov-num">{c.covered}<small> / {c.checked}</small></span>
            <span className="rsk-cov-pct">{pct}%</span>
          </div>
          <div className="rsk-cov-bar" role="img" aria-label={`${pct}% of mitigations have a test`}>
            <i style={{ width: `${pct}%` }} />
          </div>
          <div className="rsk-fine">
            {c.uncovered > 0 ? <><strong>{c.uncovered}</strong> mitigation{c.uncovered === 1 ? ' has' : 's have'} no test</> : 'Every mitigation has a test'}
            {c.unchecked > 0 && <> · {c.unchecked} not checked yet</>}
          </div>
        </>
      )}
      {c.tests > 0 && (
        <div className="rsk-cov-runs">
          <div className="rsk-cov-sub">Last run of their {c.tests} test{c.tests === 1 ? '' : 's'}</div>
          <div className="rsk-cov-stack" role="img" aria-label={parts.map(([k, l]) => `${res[k]} ${l}`).join(', ')}>
            {parts.map(([k, , color]) => <i key={k} style={{ flex: res[k], background: color }} />)}
          </div>
          <div className="rsk-cov-legend">
            {parts.map(([k, label, color]) => (
              <span key={k}><i style={{ background: color }} />{res[k]} {label}</span>
            ))}
          </div>
          {!verification && res.unknown > 0 && (
            <div className="rsk-fine">Add a test filter with Last Test Run Result to the panel to read the results.</div>
          )}
        </div>
      )}
    </div>
  );
}

export function SettingsLine({ settings, onRefresh, busy }) {
  if (!settings) return null;
  const src = settings.source === 'orcanos' ? 'Orcanos FMEA settings'
    : settings.source === 'cached' ? 'Orcanos FMEA settings (saved copy)'
      : 'default 5 × 5 table';
  const how = settings.method === 'scalar'
    ? `scalar RPN = P × S${settings.use_detectable ? ' × D' : ''}`
    : `tabular P × S${settings.use_phz_phm ? ', P = P-hazard × P-harm' : ''}`;
  return (
    <span className="rsk-settings-line">
      Scoring: <strong>{src}</strong> ({how}, 1–{settings.range || 5})
      {onRefresh && (
        <button type="button" className="drv-link rsk-small-link" onClick={onRefresh} disabled={busy}>
          {busy ? 'reading…' : 'refresh settings'}
        </button>
      )}
    </span>
  );
}

/**
 * The folded card's one-line strip: score, risk levels after mitigation, control status and
 * test coverage. Clicking a control count unfolds the card filtered to it.
 */
export function FoldedMetrics({ summary: s, onPickControl }) {
  const score = s.score;
  const cov = s.coverage;
  return (
    <div className="rsk-fold">
      <div className="rsk-fold-item">
        <span className="rsk-fold-lbl">Risk score</span>
        {score && score.score !== null && score.score !== undefined ? (
          <span className="rsk-fold-val">
            <strong>{health(score.score)}</strong><small>/100</small>
            <span className="rsk-pill" style={BAND_STYLE[score.band]}>{score.band}</span>
          </span>
        ) : <span className="rsk-dim">—</span>}
      </div>
      <div className="rsk-fold-item">
        <span className="rsk-fold-lbl">Levels after mitigation</span>
        <LevelLegend levels={s.levels_post} />
      </div>
      <div className="rsk-fold-item">
        <span className="rsk-fold-lbl">Control</span>
        <span className="rsk-fold-ctl">
          {(s.control || []).filter((c) => c.n > 0).map((c) => {
            const st = CONTROL_STYLE[c.status] || CONTROL_STYLE.unscored;
            return (
              <button type="button" key={c.status} className="rsk-fold-chip" title={c.hint}
                onClick={() => onPickControl(c.status)}>
                <i style={{ background: st.dot }} />{c.label}<strong>{c.n}</strong>
              </button>
            );
          })}
        </span>
      </div>
      <div className="rsk-fold-item">
        <span className="rsk-fold-lbl">Mitigations with a test</span>
        {cov && cov.checked > 0
          ? <span className="rsk-fold-val"><strong>{cov.covered}</strong><small> / {cov.checked}</small> <span className="rsk-dim">· {cov.pct}%</span></span>
          : <span className="rsk-dim">—</span>}
      </div>
    </div>
  );
}
